import React, { useEffect, useState } from 'react';
import { Container, Box, Typography, Card, CardContent, TextField, Button, Alert, FormControlLabel, Switch, CircularProgress } from '@mui/material';
import { Settings as SettingsIcon, Save as SaveIcon } from '@mui/icons-material';
import { settingsService } from '../../services/firebase';
import { languageSettingsService } from '../../services/languageSettingsService';

const ManageSiteSettingsPage: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [whatsappNumber, setWhatsappNumber] = useState('');
  const [languageVisibility, setLanguageVisibility] = useState<{ tr: boolean; en: boolean; ar: boolean }>({ tr: true, en: true, ar: true });

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const settings = await settingsService.getSettings();
        if (settings?.whatsappNumber) setWhatsappNumber(settings.whatsappNumber);
        const langSettings = await languageSettingsService.getLanguageSettings();
        if (langSettings?.languages) setLanguageVisibility(langSettings.languages);
      } catch (err) {
        setError('Ayarlar yüklenirken hata oluştu');
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const handleSave = async () => {
    try {
      setSaving(true);
      setError(null);
      await settingsService.updateSettings({ whatsappNumber });
      await languageSettingsService.updateLanguageSettings({ languages: languageVisibility });
      setSuccess('Ayarlar başarıyla kaydedildi');
    } catch (err) {
      setError('Ayarlar kaydedilirken hata oluştu');
    } finally {
      setSaving(false);
    }
  };

  const toggleLanguage = (key: 'tr' | 'en' | 'ar') => {
    setLanguageVisibility(prev => ({ ...prev, [key]: !prev[key] }));
  };

  if (loading) {
    return (
      <Container maxWidth="lg">
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '50vh' }}>
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg">
      <Box sx={{ mb: 4 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
          <SettingsIcon sx={{ fontSize: 40, color: 'primary.main' }} />
          <Typography variant="h4">Site Ayarları</Typography>
        </Box>
        <Typography variant="body1" color="text.secondary">
          WhatsApp numarasını ve dil görünürlüğünü yönetin.
        </Typography>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 3 }}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 3 }} onClose={() => setSuccess(null)}>{success}</Alert>}

      <Card sx={{ mb: 4 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>WhatsApp Numarası</Typography>
          <TextField
            fullWidth
            label="WhatsApp Numarası"
            value={whatsappNumber}
            onChange={e => setWhatsappNumber(e.target.value)}
            placeholder="Örn: +905551234567"
            sx={{ maxWidth: 400, mb: 2 }}
          />
        </CardContent>
      </Card>

      <Card sx={{ mb: 4 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>Dil Görünürlüğü</Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            <FormControlLabel control={<Switch checked={languageVisibility.tr} onChange={() => toggleLanguage('tr')} />} label="Türkçe" />
            <FormControlLabel control={<Switch checked={languageVisibility.en} onChange={() => toggleLanguage('en')} />} label="İngilizce" />
            <FormControlLabel control={<Switch checked={languageVisibility.ar} onChange={() => toggleLanguage('ar')} />} label="Arapça" />
          </Box>
        </CardContent>
      </Card>

      <Button variant="contained" startIcon={<SaveIcon />} onClick={handleSave} disabled={saving}>
        Kaydet
      </Button>
    </Container>
  );
};

export default ManageSiteSettingsPage; 